import React from 'react';
import { formatCompactStorePrice, getPackDescription, getPackId, getPackImage, getPackName } from './packSelectorUtils';

const QuestPackSelector = ({
  questPacks,
  selectedPack,
  onSelectPack,
  onOpenLockedPack,
  disabled,
  conversationStarted,
  loading = false,
  errorMessage = '',
}) => {
  if (conversationStarted) {
    const selectedQuestPack = (questPacks || []).find((pack) => getPackId(pack) === selectedPack);
    const displayText = selectedQuestPack
      ? getPackName(selectedQuestPack)
      : selectedPack;

    return (
      <div className="cq-pack-display">
        <p className="cq-pack-display__label">Quest Pack</p>
        <p className="cq-pack-display__name">{displayText || 'Not selected'}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="cq-quest-pack-feedback" role="status">
        Loading Quest Packs...
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="cq-quest-pack-feedback is-error" role="status">
        {errorMessage}
      </div>
    );
  }

  if (!questPacks?.length) {
    return (
      <div className="cq-quest-pack-feedback" role="status">
        No Quest Packs available for this Game.
      </div>
    );
  }

  return (
    <div className="cq-quest-pack-list" role="listbox" aria-label="Quest Pack selector">
      {questPacks.map((pack) => {
        const packId = getPackId(pack);
        const isSelected = selectedPack === packId;
        const isLocked = Boolean(pack.is_locked);
        const buyLabel = formatCompactStorePrice(pack.price_usd);
        const primaryBadge = isLocked
          ? (buyLabel ? `Buy · ${buyLabel}` : 'Buy')
          : isSelected
            ? 'Selected'
            : 'Owned';
        const showNewBadge = Boolean(pack.is_owned && pack.is_unplayed);
        const cardClassName = [
          'cq-quest-pack-card',
          isSelected ? 'is-selected' : '',
          isLocked ? 'is-locked' : '',
          showNewBadge ? 'is-unplayed' : '',
        ].filter(Boolean).join(' ');

        return (
          <button
            key={packId}
            type="button"
            className={cardClassName}
            onClick={() => (isLocked ? onOpenLockedPack?.(pack) : onSelectPack?.(packId))}
            disabled={disabled}
            aria-pressed={isSelected}
          >
            {showNewBadge ? (
              <>
                <span className="cq-visually-hidden">New</span>
                <img
                  className="cq-quest-pack-card__new-banner"
                  src="/images/new-ribbon.png"
                  alt=""
                  aria-hidden="true"
                />
              </>
            ) : null}

            <span className="cq-quest-pack-card__body">
              <span className="cq-quest-pack-card__topline">
                <span className="cq-quest-pack-card__title">{getPackName(pack)}</span>
                <span className="cq-badge-row cq-badge-row--quest-pack">
                  <span
                    className={`cq-status-badge${primaryBadge === 'Selected' ? ' is-selected' : ''}${isLocked ? ' is-locked' : ''}`}
                  >
                    {primaryBadge}
                  </span>
                </span>
              </span>

              <span className="cq-quest-pack-card__description">
                {pack.display_description || getPackDescription(pack) || 'Fresh chaos. No filter.'}
              </span>
            </span>

            <span className="cq-quest-pack-card__art-wrap" aria-hidden="true">
              {getPackImage(pack) ? (
                <img className="cq-quest-pack-card__art" src={getPackImage(pack)} alt="" />
              ) : (
                <span className="cq-quest-pack-card__chip cq-chip-badge cq-chip-badge--image-only" />
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default QuestPackSelector;
